import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import {
  PointerEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Stack } from 'src/core/ui';

import * as styles from './InventoryEventPage.css';

export const TABS_NONE = 'none';

type PointerEventLevel = 'root' | 'star' | undefined;

export function TabWithStarIcon<TTabs>({
  children,
  onIconClick,
  defaultTab,
  tabType,
  persist,
  className,
  avoidPointerEvents,
}: {
  children: ReactNode;
  onIconClick: (value: TTabs) => void;
  defaultTab?: TTabs | undefined;
  tabType: TTabs;
  persist?: boolean;
  className?: string;
  avoidPointerEvents?: PointerEventLevel;
}) {
  const [iconHovered, setIconHovered] = useState(false);

  const handlerPointerEvent = useCallback(
    (source: 'root' | 'star'): PointerEventHandler<HTMLDivElement> =>
      (e) => {
        if (!avoidPointerEvents) {
          return;
        }
        if (
          avoidPointerEvents === 'root' ||
          (avoidPointerEvents === 'star' && source === 'star')
        ) {
          e.stopPropagation();
          e.preventDefault();
        }
      },
    [avoidPointerEvents]
  );

  // Reset hovered status when switching default tabs
  useEffect(() => {
    setTimeout(() => {
      setIconHovered(false);
    }, 50);
  }, [defaultTab]);

  return (
    <Stack
      direction="row"
      gap="m"
      alignItems="center"
      className={clsx(styles.tabHover, className)}
      onPointerDown={handlerPointerEvent('root')}
      onPointerUp={handlerPointerEvent('root')}
    >
      <div>{children}</div>
      {defaultTab === tabType ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            onIconClick(TABS_NONE as TTabs);
          }}
          onPointerDown={handlerPointerEvent('star')}
          onPointerUp={handlerPointerEvent('star')}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <StarIconSolid className={`${styles.defaultTabIcon}`} />
        </div>
      ) : (
        <div
          onMouseEnter={() => setIconHovered(true)}
          onMouseLeave={() => setIconHovered(false)}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            onIconClick(tabType);
          }}
          onPointerDown={handlerPointerEvent('star')}
          onPointerUp={handlerPointerEvent('star')}
          className={clsx({
            [styles.hoverIconContainer]: !persist,
          })}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {iconHovered ? (
            <StarIconSolid
              className={clsx(styles.icon, styles.iconVisible, {
                [styles.iconPersist]: persist,
              })}
            />
          ) : (
            <StarIconOutline
              className={clsx(styles.icon, styles.iconVisible, {
                [styles.iconPersist]: persist,
              })}
            />
          )}
        </div>
      )}
    </Stack>
  );
}
